import { productForHost } from './productBoundary';
// =============================================================================
// HOST → TENANT MAPPING
//
// Quote-Goat / Realtimax serves multiple tenants from the same codebase. The
// auth user may belong to several tenant_members rows; without a hint, the
// app can otherwise inherit another product’s last selection.
//
// This map gives the loader a deterministic answer per host: realtimax.com
// loads the flooring tenant, *.blissbunkbeds.com loads Bliss Bedrooms LLC,
// and anything unmatched retains legacy selection behavior. Registered product
// hosts do not fall back to an unrelated business if their business is missing.
// =============================================================================

import { resolveTheme, type ThemePresetId } from './themes';

export interface HostTenantRule {
  /** Returns true if the given hostname should load this tenant slug. */
  match: (hostname: string) => boolean;
  /** `slug` column of the `tenants` table. */
  slug: string;
}

export const HOST_TENANT_MAP: HostTenantRule[] = [
  { match: (h) => productForHost(h) === 'realtimax',     slug: 'renovation-flooring' },
  { match: (h) => productForHost(h) === 'bliss', slug: 'bliss-bedrooms' },
];

export function tenantSlugForHost(hostname: string): string | null {
  for (const rule of HOST_TENANT_MAP) {
    if (rule.match(hostname)) return rule.slug;
  }
  return null;
}

export function isBlissTenantSlug(slug: string | null | undefined): boolean {
  if (!slug) return false;
  return slug.startsWith('bliss-') || slug.includes('bliss');
}

/** Branding fallbacks when tenants.logo_url is empty in Supabase. */
export const TENANT_BRANDING_FALLBACKS: Record<
  string,
  { logoUrl?: string; supportEmail?: string }
> = {
  'bliss-bedrooms': {
    logoUrl:
      'https://www.blissbunkbeds.com/wp-content/uploads/2023/02/Bliss-Bunk-Beds-logo-108-x-105px.png',
    supportEmail: 'jason@blissbunkbeds.com',
  },
  'bliss-bunk-co': {
    logoUrl:
      'https://www.blissbunkbeds.com/wp-content/uploads/2023/02/Bliss-Bunk-Beds-logo-108-x-105px.png',
    supportEmail: 'jason@blissbunkbeds.com',
  },
  'renovation-flooring': {
    logoUrl: '/renovation-flooring-logo.png',
    supportEmail: 'info@renovationflooring.com',
  },
};

export function brandingFallbackForSlug(slug: string | null | undefined) {
  if (!slug) return {};
  if (isBlissTenantSlug(slug)) {
    return TENANT_BRANDING_FALLBACKS['bliss-bedrooms'];
  }
  return TENANT_BRANDING_FALLBACKS[slug] ?? {};
}

/** Host-mapped tenants with a locked brand preset (ignores DB primary_color). */
const LOCKED_TENANT_THEMES: Record<string, ThemePresetId> = {
  'bliss-bedrooms': 'classic-gold',
  'bliss-bunk-co': 'classic-gold',
};

export function resolveThemeForTenant(
  tenantSlug: string | null | undefined,
  presetId?: string | null,
  customPrimary?: string | null,
) {
  if (isBlissTenantSlug(tenantSlug)) {
    return resolveTheme('classic-gold', null);
  }
  const locked = tenantSlug ? LOCKED_TENANT_THEMES[tenantSlug] : undefined;
  if (locked) return resolveTheme(locked, null);
  return resolveTheme(presetId, customPrimary);
}

